// src/utils/mockDataWHS.ts
// Mock data for WHS modules: Permits to Work, Site Inductions, HSEQ Inspections
// All IDs consistent with existing mockData.ts (j1-j6, e1-e8)

// ─────────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────────

export type PermitType =
  | 'ENERGISED_ELECTRICAL'
  | 'EXCAVATION'
  | 'OVERHEAD_LINES'
  | 'CONFINED_SPACE'
  | 'HOT_WORKS'
  | 'WORKING_AT_HEIGHTS'
  | 'TRAFFIC_MANAGEMENT'
  | 'LOTO'

export type PermitStatus =
  | 'DRAFT'
  | 'PENDING'
  | 'SUBMITTED'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED'
  | 'EXPIRED'

export type InductionStatus = 'PENDING' | 'IN_PROGRESS' | 'PASSED' | 'FAILED' | 'EXPIRED' | 'INDUCTED' | 'INDUCTED'
export type InductionType   = 'COMPANY'  | 'PROJECT' | 'CLIENT_SPECIFIC'

export type InspectionType   = 'DAILY_PRESTART' | 'SAFETY_WALK' | 'PLANT_INSPECTION' | 'ELECTRICAL_INSTALL' | 'EXCAVATION_CHECK' | 'CONFINED_SPACE_PREENTRY' | 'WEEKLY_SITE_AUDIT'
export type InspectionResult = 'PASS' | 'FAIL' | 'CONDITIONAL'
export type IssueStatus      = 'OPEN' | 'IN_PROGRESS' | 'CLOSED'
export type IssueSeverity    = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

// ─────────────────────────────────────────────────────────────────
// PERMIT TO WORK
// ─────────────────────────────────────────────────────────────────

export const PERMIT_TYPE_META: Record<PermitType, { label: string; icon: string; color: string; bg: string; requiredCredentials: string[] }> = {
  ENERGISED_ELECTRICAL: { label: 'Energised Electrical Work',   icon: '⚡', color: 'text-yellow-700', bg: 'bg-yellow-50',  requiredCredentials: ['electricalLicenceNumber', 'hvAuthority'] },
  EXCAVATION:           { label: 'Excavation / Ground Pen.',     icon: '🏗', color: 'text-orange-700', bg: 'bg-orange-50',  requiredCredentials: [] },
  OVERHEAD_LINES:       { label: 'Working Near Overhead Lines',  icon: '🔌', color: 'text-red-700',    bg: 'bg-red-50',     requiredCredentials: ['electricalLicenceNumber'] },
  CONFINED_SPACE:       { label: 'Confined Space Entry',         icon: '🕳', color: 'text-purple-700', bg: 'bg-purple-50',  requiredCredentials: ['confinedSpaceEntry'] },
  HOT_WORKS:            { label: 'Hot Works',                    icon: '🔥', color: 'text-red-700',    bg: 'bg-red-50',     requiredCredentials: [] },
  WORKING_AT_HEIGHTS:   { label: 'Working at Heights',           icon: '📏', color: 'text-blue-700',   bg: 'bg-blue-50',    requiredCredentials: ['workingAtHeights'] },
  TRAFFIC_MANAGEMENT:   { label: 'Traffic Management / Road',    icon: '🚦', color: 'text-teal-700',   bg: 'bg-teal-50',    requiredCredentials: ['tcaExpiry'] },
  LOTO:                 { label: 'Lockout / Tagout (LOTO)',      icon: '🔒', color: 'text-gray-700',   bg: 'bg-gray-50',    requiredCredentials: ['electricalLicenceNumber'] },
}

export interface PermitWorker {
  employeeId: string
  name: string
  role: string
  credentialStatus: 'OK' | 'MISSING' | 'EXPIRED'
  missingCredentials: string[]
}

export interface PreStartItem {
  id: string
  label: string
  result: 'OK' | 'FAIL' | 'NA' | null
  notes?: string
}

export interface Permit {
  id: string
  permitNumber: string
  type: PermitType
  jobId: string
  jobNumber: string
  status: PermitStatus
  zone: string
  location: string
  plannedStart: string
  plannedEnd: string
  actualStart?: string
  actualEnd?: string
  description: string
  // Links
  linkedSwmsId?: string
  linkedSwmsNumber?: string
  linkedTmpId?: string
  // Applicant
  submittedById: string
  submittedByName: string
  submittedAt: string
  // Approval
  approvedById?: string
  approvedByName?: string
  approvedAt?: string
  rejectionReason?: string
  // Workers on permit
  workers: PermitWorker[]
  // Pre-start checklist
  preStartItems: PreStartItem[]
  preStartSignedAt?: string
  preStartSignedByName?: string
  // Close-out
  closeOutItems: PreStartItem[]
  closeOutSignedAt?: string
  closeOutSignedByName?: string
  closeOutNotes?: string
  // BYDA (excavation permits)
  bydaReference?: string
  // Energex (overhead/energised)
  energexNotified?: boolean
  energexReference?: string
  // Atmospheric test (confined space)
  o2Level?: number
  coLevel?: number
  methaneLevel?: number
  h2sLevel?: number
}

const EXCAVATION_PRESTART: PreStartItem[] = [
  { id: 'ex1', label: 'BYDA plans obtained and on site', result: null },
  { id: 'ex2', label: 'Service locating completed by qualified technician', result: null },
  { id: 'ex3', label: 'Hand-dig exclusion zone (500mm from services) marked', result: null },
  { id: 'ex4', label: 'Barricading / exclusion zone established', result: null },
  { id: 'ex5', label: 'Spotter assigned for mechanical excavation', result: null },
  { id: 'ex6', label: 'SWMS reviewed and signed by all workers', result: null },
  { id: 'ex7', label: 'Traffic Management in place (if applicable)', result: null },
]

const CONFINED_SPACE_PRESTART: PreStartItem[] = [
  { id: 'cs1', label: 'Atmospheric test completed: O₂ 19.5–23.5%', result: null },
  { id: 'cs2', label: 'CO reading < 25 ppm', result: null },
  { id: 'cs3', label: 'H₂S reading < 1 ppm', result: null },
  { id: 'cs4', label: 'Methane / LEL reading < 10%', result: null },
  { id: 'cs5', label: 'Forced ventilation running (if required)', result: null },
  { id: 'cs6', label: 'Standby person stationed outside', result: null },
  { id: 'cs7', label: 'Rescue equipment on hand', result: null },
  { id: 'cs8', label: 'Communications established (radio/phone)', result: null },
]

const ENERGISED_PRESTART: PreStartItem[] = [
  { id: 'en1', label: 'Switching programme obtained from Energex / supply authority', result: null },
  { id: 'en2', label: 'SP (Switching Programme) number recorded on permit', result: null },
  { id: 'en3', label: 'LOTO applied — lock and tag on isolator', result: null },
  { id: 'en4', label: 'Voltage tested DEAD before touching (test-before-touch)', result: null },
  { id: 'en5', label: 'Earth clamps applied', result: null },
  { id: 'en6', label: 'HV Authority holder on site', result: null },
  { id: 'en7', label: 'Arc flash rated PPE worn', result: null },
  { id: 'en8', label: 'Safety observer assigned and briefed', result: null },
]

const HOT_WORKS_PRESTART: PreStartItem[] = [
  { id: 'hw1', label: 'Flammable materials cleared 3m radius', result: null },
  { id: 'hw2', label: 'Fire extinguisher within reach (correct class)', result: null },
  { id: 'hw3', label: 'Fire watch assigned for duration + 30min post work', result: null },
  { id: 'hw4', label: 'Hot work area barricaded', result: null },
  { id: 'hw5', label: 'Welding screens / flash barriers in place', result: null },
  { id: 'hw6', label: 'PPE: welding helmet, gloves, leather apron', result: null },
]

export const MOCK_PERMITS: Permit[] = [
  {
    id: 'ptw1',
    permitNumber: 'PTW-2026-001',
    type: 'CONFINED_SPACE',
    jobId: 'j4',
    jobNumber: 'TP-2026-004',
    status: 'IN_PROGRESS',
    zone: 'Annerley Substation — Cable Pits A-D',
    location: 'Pit A, cnr Ipswich Rd & Annerley Rd',
    plannedStart: '2026-06-20T07:00:00Z',
    plannedEnd: '2026-06-20T17:00:00Z',
    actualStart: '2026-06-20T07:18:00Z',
    description: 'Entry to HV cable pits A-D for 11kV cable jointing and termination works. All pits to be atmospheric tested before entry.',
    linkedSwmsId: 'swms2',
    linkedSwmsNumber: 'SWMS-2026-004',
    submittedById: 'e3',
    submittedByName: 'Sam Nguyen',
    submittedAt: '2026-06-19T15:30:00Z',
    approvedById: 'e8',
    approvedByName: 'Sarah Kim (WHSO)',
    approvedAt: '2026-06-19T16:45:00Z',
    workers: [
      { employeeId: 'e3', name: 'Sam Nguyen', role: 'HV Technician', credentialStatus: 'OK', missingCredentials: [] },
    ],
    preStartItems: CONFINED_SPACE_PRESTART.map(i => ({
      ...i,
      result: i.id === 'cs1' ? 'OK' : i.id === 'cs2' ? 'OK' : i.id === 'cs3' ? 'OK' : i.id === 'cs4' ? 'OK' : i.id === 'cs5' ? 'OK' : i.id === 'cs6' ? 'OK' : i.id === 'cs7' ? 'OK' : 'OK',
    })),
    preStartSignedAt: '2026-06-20T07:15:00Z',
    preStartSignedByName: 'Sam Nguyen',
    closeOutItems: CONFINED_SPACE_PRESTART.map(i => ({ ...i, result: null })),
    o2Level: 20.9, coLevel: 0, methaneLevel: 0, h2sLevel: 0,
    bydaReference: undefined,
    energexNotified: false,
  },
  {
    id: 'ptw2',
    permitNumber: 'PTW-2026-002',
    type: 'EXCAVATION',
    jobId: 'j1',
    jobNumber: 'TP-2026-001',
    status: 'APPROVED',
    zone: 'Kessels Rd / Klumpp Rd — Pits 7-12',
    location: 'Kessels Rd northbound median',
    plannedStart: '2026-06-21T06:00:00Z',
    plannedEnd: '2026-06-21T16:00:00Z',
    description: 'Excavation for traffic signal pit installation — pits 7-12. Mechanical excavation with spotter. Multiple services confirmed in zone from BYDA.',
    linkedSwmsId: 'swms1',
    linkedSwmsNumber: 'SWMS-2026-001',
    submittedById: 'e1',
    submittedByName: 'Tony Rivera',
    submittedAt: '2026-06-20T14:00:00Z',
    approvedById: 'e8',
    approvedByName: 'Sarah Kim (WHSO)',
    approvedAt: '2026-06-20T15:30:00Z',
    workers: [
      { employeeId: 'e1', name: 'Tony Rivera', role: 'Electrician A-Grade', credentialStatus: 'OK', missingCredentials: [] },
      { employeeId: 'e6', name: 'Dave Fielding', role: 'Apprentice Yr3', credentialStatus: 'OK', missingCredentials: [] },
    ],
    preStartItems: EXCAVATION_PRESTART,
    closeOutItems: EXCAVATION_PRESTART.map(i => ({ ...i, result: null })),
    bydaReference: 'BYDA-2026-0061234',
  },
  {
    id: 'ptw3',
    permitNumber: 'PTW-2026-003',
    type: 'ENERGISED_ELECTRICAL',
    jobId: 'j4',
    jobNumber: 'TP-2026-004',
    status: 'SUBMITTED',
    zone: 'Annerley Substation — 11kV Switchroom',
    location: 'Annerley Substation switchroom, panel C',
    plannedStart: '2026-06-22T08:00:00Z',
    plannedEnd: '2026-06-22T14:00:00Z',
    description: 'Live adjacent 11kV cable termination in switchroom panel C. Cable under work de-energised via formal switching programme. Adjacent panels remain live.',
    linkedSwmsId: 'swms2',
    linkedSwmsNumber: 'SWMS-2026-004',
    submittedById: 'e3',
    submittedByName: 'Sam Nguyen',
    submittedAt: '2026-06-20T16:00:00Z',
    workers: [
      { employeeId: 'e3', name: 'Sam Nguyen', role: 'HV Technician', credentialStatus: 'OK', missingCredentials: [] },
    ],
    preStartItems: ENERGISED_PRESTART,
    closeOutItems: ENERGISED_PRESTART.map(i => ({ ...i, result: null })),
    energexNotified: true,
    energexReference: 'SP-2026-04881',
  },
  {
    id: 'ptw4',
    permitNumber: 'PTW-2026-004',
    type: 'HOT_WORKS',
    jobId: 'j1',
    jobNumber: 'TP-2026-001',
    status: 'COMPLETED',
    zone: 'Kessels Rd — Signal Pole Fabrication Bay',
    location: 'Site compound / fabrication area',
    plannedStart: '2026-06-18T08:00:00Z',
    plannedEnd: '2026-06-18T12:00:00Z',
    actualStart: '2026-06-18T08:10:00Z',
    actualEnd: '2026-06-18T11:45:00Z',
    description: 'Welding signal pole base plates and cutting service conduit ends. Steel fabrication for signal arm brackets.',
    linkedSwmsId: 'swms1',
    linkedSwmsNumber: 'SWMS-2026-001',
    submittedById: 'e1',
    submittedByName: 'Tony Rivera',
    submittedAt: '2026-06-17T16:00:00Z',
    approvedById: 'e8',
    approvedByName: 'Sarah Kim (WHSO)',
    approvedAt: '2026-06-17T16:30:00Z',
    workers: [
      { employeeId: 'e1', name: 'Tony Rivera', role: 'Electrician A-Grade', credentialStatus: 'OK', missingCredentials: [] },
    ],
    preStartItems: HOT_WORKS_PRESTART.map(i => ({ ...i, result: 'OK' as 'OK' })),
    preStartSignedAt: '2026-06-18T08:05:00Z',
    preStartSignedByName: 'Tony Rivera',
    closeOutItems: HOT_WORKS_PRESTART.map(i => ({ ...i, result: 'OK' as 'OK' })),
    closeOutSignedAt: '2026-06-18T11:50:00Z',
    closeOutSignedByName: 'Tony Rivera',
    closeOutNotes: 'All clear. Fire watch completed 30 min post-welding. No issues.',
  },
]

// ─────────────────────────────────────────────────────────────────
// SITE INDUCTIONS
// ─────────────────────────────────────────────────────────────────

export interface InductionQuestion {
  id: string
  text: string
  options: string[]
  correctIndex: number
}

export interface InductionModule {
  id: string
  type: InductionType
  jobId?: string       // null for COMPANY type
  jobNumber?: string
  title: string
  description: string
  version: number
  questions: InductionQuestion[]
  passMark: number     // percentage
}

export interface WorkerInduction {
  id: string
  inductionModuleId: string
  inductionTitle: string
  inductionType: InductionType
  jobId?: string
  jobNumber?: string
  workerId: string     // could be employee or external worker
  workerName: string
  workerMobile: string
  workerType: 'EMPLOYEE' | 'SUBCONTRACTOR' | 'VISITOR'
  company?: string
  // Credential snapshot at time of induction
  whiteCardNumber?: string
  whiteCardExpiry?: string
  electricalLicenceNumber?: string
  electricalLicenceExpiry?: string
  // Induction result
  status: InductionStatus
  uniqueWorkerId?: string   // 6-digit code issued on completion
  startedAt?: string
  completedAt?: string
  score?: number            // percentage
  passed?: boolean
  expiryDate?: string       // Induction expiry (typically 12 months)
  inductedById?: string     // If done face-to-face
  inductedByName?: string
  notes?: string
}

export const MOCK_INDUCTION_MODULES: InductionModule[] = [
  {
    id: 'ind_co1',
    type: 'COMPANY',
    title: 'MCB Civil Electrical — Company Induction',
    description: 'Mandatory for all workers, employees, and contractors before attending any MCBCE project site.',
    version: 3,
    passMark: 80,
    questions: [
      {
        id: 'q1',
        text: 'What is the emergency assembly point procedure if the site evacuation alarm sounds?',
        options: ['Continue working until directly told to stop', 'Proceed to the designated muster point immediately and await roll call', 'Call your supervisor first', 'Turn off all equipment and wait in place'],
        correctIndex: 1,
      },
      {
        id: 'q2',
        text: 'Under the WHS Act, which of the following is the FIRST priority in the hierarchy of controls?',
        options: ['Personal Protective Equipment (PPE)', 'Administrative controls', 'Elimination of the hazard', 'Engineering controls'],
        correctIndex: 2,
      },
      {
        id: 'q3',
        text: 'If you discover a near miss on site, what should you do?',
        options: ['Report it at the end of the day', 'Only report it if someone was injured', 'Report it immediately to your supervisor or WHSO', 'Document it in your personal notebook'],
        correctIndex: 2,
      },
      {
        id: 'q4',
        text: 'Before entering a cable pit or vault you must:',
        options: ['Check the weather', 'Complete atmospheric testing and have a standby person positioned outside', 'Notify your PM', 'Sign on to the SWMS only'],
        correctIndex: 1,
      },
      {
        id: 'q5',
        text: 'What does "Test before Touch" mean for electrical work?',
        options: ['Touch the cable gently first', 'Always test that electrical equipment is de-energised before making contact', 'Test your tools before use', 'Test the connection after touching'],
        correctIndex: 1,
      },
    ],
  },
  {
    id: 'ind_j1',
    type: 'PROJECT',
    jobId: 'j1',
    jobNumber: 'TP-2026-001',
    title: 'TP-2026-001 — Kessels/Klumpp Rd Site Induction',
    description: 'Project-specific induction for the Kessels Rd / Klumpp Rd traffic signal upgrade. Must be completed in addition to the Company Induction.',
    version: 1,
    passMark: 100,
    questions: [
      {
        id: 'jq1',
        text: 'Where is the muster point for this site?',
        options: ['Site container at the compound entrance (north gate)', 'The nearest corner footpath', 'Inside the site office', 'The TMC vehicle'],
        correctIndex: 0,
      },
      {
        id: 'jq2',
        text: 'What is the designated speed zone on Kessels Rd through our work zone?',
        options: ['60 km/h', '80 km/h', '40 km/h', '25 km/h'],
        correctIndex: 2,
      },
      {
        id: 'jq3',
        text: 'Who is the First Aider on this project?',
        options: ['Tony Rivera', 'Dave Fielding', 'Sarah Kim', 'Lisa Grant'],
        correctIndex: 0,
      },
    ],
  },
  {
    id: 'ind_j4',
    type: 'PROJECT',
    jobId: 'j4',
    jobNumber: 'TP-2026-004',
    title: 'TP-2026-004 — Annerley HV Cable Site Induction',
    description: 'High voltage project induction. Workers with HV authority only. Must complete company induction first.',
    version: 2,
    passMark: 100,
    questions: [
      {
        id: 'hq1',
        text: 'What is the exclusion zone around 11kV overhead lines on this project?',
        options: ['0.5 metres', '3 metres (or as per Energex written safety advice)', '1 metre', 'No exclusion zone required — just be careful'],
        correctIndex: 1,
      },
      {
        id: 'hq2',
        text: 'Before any HV cable work begins on this site, a switching programme must be:',
        options: ['Requested verbally from Energex on the day', 'Obtained from Energex control room and the SP number recorded on the PTW', 'Approved by the site manager only', 'Not required for de-energised cable'],
        correctIndex: 1,
      },
    ],
  },
]

export const MOCK_WORKER_INDUCTIONS: WorkerInduction[] = [
  {
    id: 'wi1', inductionModuleId: 'ind_co1', inductionTitle: 'MCB Civil Electrical — Company Induction',
    inductionType: 'COMPANY', workerId: 'e1', workerName: 'Tony Rivera',
    workerMobile: '0412 345 678', workerType: 'EMPLOYEE', company: 'MCB Civil Electrical',
    electricalLicenceNumber: '88123QLD', electricalLicenceExpiry: '2027-06-30',
    whiteCardNumber: 'WC-QLD-7741239', whiteCardExpiry: '2027-01-10',
    status: 'PASSED', uniqueWorkerId: '417823', score: 100, passed: true,
    startedAt: '2026-01-10T08:00:00Z', completedAt: '2026-01-10T08:22:00Z',
    expiryDate: '2027-01-10',
  },
  {
    id: 'wi2', inductionModuleId: 'ind_j1', inductionTitle: 'TP-2026-001 — Kessels/Klumpp Rd Site Induction',
    inductionType: 'PROJECT', jobId: 'j1', jobNumber: 'TP-2026-001',
    workerId: 'e1', workerName: 'Tony Rivera', workerMobile: '0412 345 678',
    workerType: 'EMPLOYEE', company: 'MCB Civil Electrical',
    status: 'PASSED', uniqueWorkerId: '417823', score: 100, passed: true,
    startedAt: '2026-06-15T07:30:00Z', completedAt: '2026-06-15T07:41:00Z',
    expiryDate: '2027-06-15',
  },
  {
    id: 'wi3', inductionModuleId: 'ind_j4', inductionTitle: 'TP-2026-004 — Annerley HV Cable Site Induction',
    inductionType: 'PROJECT', jobId: 'j4', jobNumber: 'TP-2026-004',
    workerId: 'e3', workerName: 'Sam Nguyen', workerMobile: '0434 567 890',
    workerType: 'EMPLOYEE', company: 'MCB Civil Electrical',
    electricalLicenceNumber: '99241QLD', electricalLicenceExpiry: '2028-03-15',
    status: 'PASSED', uniqueWorkerId: '892034', score: 100, passed: true,
    startedAt: '2026-06-19T07:00:00Z', completedAt: '2026-06-19T07:18:00Z',
    expiryDate: '2027-06-19',
  },
  {
    id: 'wi4', inductionModuleId: 'ind_j1', inductionTitle: 'TP-2026-001 — Kessels/Klumpp Rd Site Induction',
    inductionType: 'PROJECT', jobId: 'j1', jobNumber: 'TP-2026-001',
    workerId: 'ext_001', workerName: 'Brad Thomson', workerMobile: '0455 123 456',
    workerType: 'SUBCONTRACTOR', company: 'Thomson Electrical Pty Ltd',
    whiteCardNumber: 'WC-QLD-5512876', whiteCardExpiry: '2026-07-22',
    electricalLicenceNumber: '77432QLD', electricalLicenceExpiry: '2027-02-28',
    status: 'PASSED', uniqueWorkerId: '334512', score: 100, passed: true,
    startedAt: '2026-06-20T06:45:00Z', completedAt: '2026-06-20T06:58:00Z',
    expiryDate: '2027-06-20',
  },
  {
    id: 'wi5', inductionModuleId: 'ind_co1', inductionTitle: 'MCB Civil Electrical — Company Induction',
    inductionType: 'COMPANY', workerId: 'ext_002', workerName: 'Pham Van Duc',
    workerMobile: '0466 789 012', workerType: 'SUBCONTRACTOR', company: 'VD Civil Pty Ltd',
    status: 'FAILED', score: 60, passed: false,
    startedAt: '2026-06-20T09:00:00Z', completedAt: '2026-06-20T09:25:00Z',
  },
  {
    id: 'wi6', inductionModuleId: 'ind_co1', inductionTitle: 'MCB Civil Electrical — Company Induction',
    inductionType: 'COMPANY', workerId: 'ext_003', workerName: 'Jamal Williams',
    workerMobile: '0477 334 556', workerType: 'SUBCONTRACTOR', company: 'Williams Labour Hire',
    status: 'PENDING',
  },
]

// ─────────────────────────────────────────────────────────────────
// HSEQ INSPECTIONS & OBSERVATIONS
// ─────────────────────────────────────────────────────────────────

export interface InspectionCheckItem {
  id: string
  label: string
  category: string
  result: 'OK' | 'ISSUE' | 'NA' | null
  notes?: string
  photoKey?: string
}

export interface InspectionIssue {
  id: string
  description: string
  severity: IssueSeverity
  category: string
  location: string
  photoKey?: string
  assignedToName?: string
  assignedToId?: string
  dueDate?: string
  status: IssueStatus
  closeOutNotes?: string
  closedAt?: string
  closedByName?: string
}

export interface WHSInspection {
  id: string
  inspectionNumber: string
  type: InspectionType
  jobId: string
  jobNumber: string
  conductedById: string
  conductedByName: string
  conductedAt: string
  result: InspectionResult | null
  items: InspectionCheckItem[]
  issues: InspectionIssue[]
  positiveObservations: string[]
  notes?: string
  photoKeys?: string[]
  status: 'DRAFT' | 'SUBMITTED' | 'CLOSED'
}

export const MOCK_WHS_INSPECTIONS: WHSInspection[] = [
  {
    id: 'ins1',
    inspectionNumber: 'INS-2026-001',
    type: 'DAILY_PRESTART',
    jobId: 'j1',
    jobNumber: 'TP-2026-001',
    conductedById: 'e8',
    conductedByName: 'Sarah Kim',
    conductedAt: '2026-06-20T06:30:00Z',
    result: 'CONDITIONAL',
    status: 'SUBMITTED',
    positiveObservations: ['All PPE worn correctly by incoming crew', 'Site compound neat and organised'],
    notes: 'One open issue raised — pit cover missing from Pit 5. Addressed before work commenced.',
    items: [
      { id: 'p1', label: 'PPE available and worn by all crew', category: 'PPE', result: 'OK' },
      { id: 'p2', label: 'Traffic Management in place and correct per TMP', category: 'Traffic', result: 'OK' },
      { id: 'p3', label: 'All open excavations barricaded / covered', category: 'Excavation', result: 'ISSUE', notes: 'Pit 5 cover missing — raised as issue INS-ISS-001' },
      { id: 'p4', label: 'Plant pre-start checks completed', category: 'Plant', result: 'OK' },
      { id: 'p5', label: 'SWMS on site and accessible', category: 'Safety', result: 'OK' },
      { id: 'p6', label: 'Emergency contacts posted and visible', category: 'Emergency', result: 'OK' },
      { id: 'p7', label: 'First aid kit stocked and accessible', category: 'Emergency', result: 'OK' },
      { id: 'p8', label: 'Fire extinguisher in place (fabrication area)', category: 'Fire', result: 'OK' },
    ],
    issues: [
      {
        id: 'iss1',
        description: 'Pit 5 cover missing — open excavation unbarricaded overnight',
        severity: 'HIGH',
        category: 'Excavation',
        location: 'Pit 5, SE corner Kessels/Klumpp Rd',
        assignedToName: 'Tony Rivera',
        assignedToId: 'e1',
        dueDate: '2026-06-20T07:00:00Z',
        status: 'CLOSED',
        closeOutNotes: 'Steel plate cover installed and barricade erected. Corrected 06:45.',
        closedAt: '2026-06-20T06:45:00Z',
        closedByName: 'Tony Rivera',
      },
    ],
  },
  {
    id: 'ins2',
    inspectionNumber: 'INS-2026-002',
    type: 'SAFETY_WALK',
    jobId: 'j4',
    jobNumber: 'TP-2026-004',
    conductedById: 'e8',
    conductedByName: 'Sarah Kim',
    conductedAt: '2026-06-19T14:00:00Z',
    result: 'PASS',
    status: 'SUBMITTED',
    positiveObservations: ['Excellent confined space entry procedure followed', 'Gas monitors visible and being worn', 'Standby person alert and attentive'],
    notes: 'Outstanding safety performance observed from Sam Nguyen for confined space procedure.',
    items: [
      { id: 'hv1', label: 'Confined space atmospheric testing current (< 4hr)', category: 'Confined Space', result: 'OK' },
      { id: 'hv2', label: 'Standby person in position outside all pits', category: 'Confined Space', result: 'OK' },
      { id: 'hv3', label: 'LOTO locks and tags in place on switchroom', category: 'Electrical', result: 'OK' },
      { id: 'hv4', label: 'HV exclusion zone markers in place', category: 'Electrical', result: 'OK' },
      { id: 'hv5', label: 'Arc flash PPE worn for all HV work', category: 'PPE', result: 'OK' },
      { id: 'hv6', label: 'Switching programme SP number on PTW', category: 'Electrical', result: 'OK' },
    ],
    issues: [],
  },
  {
    id: 'ins3',
    inspectionNumber: 'INS-2026-003',
    type: 'DAILY_PRESTART',
    jobId: 'j1',
    jobNumber: 'TP-2026-001',
    conductedById: 'e8',
    conductedByName: 'Sarah Kim',
    conductedAt: '2026-06-21T06:15:00Z',
    result: null,
    status: 'DRAFT',
    positiveObservations: [],
    items: [
      { id: 'dp1', label: 'PPE available and worn by all crew', category: 'PPE', result: null },
      { id: 'dp2', label: 'Traffic Management in place and correct per TMP', category: 'Traffic', result: null },
      { id: 'dp3', label: 'All open excavations barricaded / covered', category: 'Excavation', result: null },
      { id: 'dp4', label: 'Plant pre-start checks completed', category: 'Plant', result: null },
      { id: 'dp5', label: 'SWMS on site and accessible', category: 'Safety', result: null },
      { id: 'dp6', label: 'Emergency contacts posted and visible', category: 'Emergency', result: null },
      { id: 'dp7', label: 'First aid kit stocked and accessible', category: 'Emergency', result: null },
    ],
    issues: [],
  },
]

// ─────────────────────────────────────────────────────────────────
// WHS NOTIFICATIONS / BULLETINS
// ─────────────────────────────────────────────────────────────────

export type NotificationSeverity = 'INFO' | 'WARNING' | 'URGENT'
export type NotificationAudience = 'ALL_SITE' | 'TRADE' | 'SPECIFIC_WORKERS'

export interface WHSNotification {
  id: string
  notificationNumber: string
  jobId?: string
  jobNumber?: string
  severity: NotificationSeverity
  audience: NotificationAudience
  targetTrade?: string
  title: string
  body: string
  sentById: string
  sentByName: string
  sentAt: string
  deliveredCount: number
  acknowledgedCount: number
  smsDispatched: boolean
}

export const MOCK_WHS_NOTIFICATIONS: WHSNotification[] = [
  {
    id: 'notif1', notificationNumber: 'NOTIF-2026-001',
    jobId: 'j1', jobNumber: 'TP-2026-001',
    severity: 'URGENT', audience: 'ALL_SITE',
    title: 'IMMEDIATE — Severe Thunderstorm Warning',
    body: 'Bureau of Meteorology has issued a Severe Thunderstorm Warning for Brisbane. All elevated work (EWPs, pole climbing, scaffold) must CEASE IMMEDIATELY. Evacuate to the site compound. Do not resume elevated work until the all-clear is issued by the site manager.',
    sentById: 'e8', sentByName: 'Sarah Kim (WHSO)',
    sentAt: '2026-06-18T13:45:00Z',
    deliveredCount: 4, acknowledgedCount: 4, smsDispatched: true,
  },
  {
    id: 'notif2', notificationNumber: 'NOTIF-2026-002',
    jobId: 'j4', jobNumber: 'TP-2026-004',
    severity: 'WARNING', audience: 'ALL_SITE',
    title: 'Near-Miss Debrief — Unmarked Service Strike',
    body: 'Following INC-2026-001 (near-miss excavator strike on unmarked LV conduit): All excavation work on this project must now use a minimum 500mm hand-dig exclusion zone from ANY service marker. DBYD plans have been updated. Please read the updated SWMS-2026-004 Rev 3 before commencing excavation today.',
    sentById: 'e8', sentByName: 'Sarah Kim (WHSO)',
    sentAt: '2026-06-16T07:00:00Z',
    deliveredCount: 1, acknowledgedCount: 1, smsDispatched: true,
  },
  {
    id: 'notif3', notificationNumber: 'NOTIF-2026-003',
    severity: 'INFO', audience: 'ALL_SITE',
    title: 'Toolbox Talk — Confined Space Reminder',
    body: 'Reminder: Toolbox talk on confined space entry procedures is scheduled for Monday 22 June 2026 at 07:00 at the Annerley sub compound. Attendance is mandatory for all workers on TP-2026-004.',
    sentById: 'e8', sentByName: 'Sarah Kim (WHSO)',
    sentAt: '2026-06-19T15:00:00Z',
    deliveredCount: 1, acknowledgedCount: 0, smsDispatched: false,
  },
]
